import marketChangePrice from '@/components/market/market-change-price/market-change-price.vue';
import constants from '@/constants';
import { buildSummary, readableContractColor } from '../h2h-summary-model';
import h2hWidgetMixin from '../h2h-widget-mixin';

let nextChartId = 0;
const CHART_WIDTH = 300;
const CHART_LABEL_GAP = 62;
const PRICE_MAX = 100;
const CHART_TOP = 10;
const CHART_HEIGHT = 85;
const END_LABEL_MIN_GAP = 22;
const END_LABEL_CENTER_BOUNDS = { min: 22, max: 84 };
const SVG_COORDINATE_PRECISION = 2;
const HOVER_DELAY_MS = 250;
const REPLAY_COOLDOWN_MS = 5000;
const ANIMATION_DURATION_MS = 3000;
const ANIMATION_VISIBILITY_THRESHOLD = 0.4;
const ANIMATION_EASING_EXPONENT = 3;
const CLOCK_INTERVAL_MS = 60000;
const AXIS_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

export default {
  components: { marketChangePrice },
  mixins: [h2hWidgetMixin],
  data() {
    return {
      constants,
      chartWidth: CHART_WIDTH,
      chartMidPrice: PRICE_MAX / 2,
      chartClipId: `h2h-chart-clip-${++nextChartId}`,
      animationProgress: 1,
      animationFrame: null,
      hoverTimer: null,
      animationFinishedAt: null,
      animationGeneration: 0,
      chartObserver: null,
      motionPreference: null,
      chartReady: false,
      clock: Date.now(),
      clockTimer: null,
    };
  },
  computed: {
    summary() {
      return this.tile
        ? buildSummary(this.tile, this.chartReady ? this.chartRecords : [], this.clock)
        : null;
    },
    hasHistory() {
      return this.summary?.series.some((series) => series.hasHistory);
    },
    axisDates() {
      if (this.summary.state === constants.MARKET_OPEN || !this.summary.hasRange) return null;
      return {
        start: AXIS_DATE_FORMAT.format(this.summary.start),
        end: AXIS_DATE_FORMAT.format(this.summary.now),
      };
    },
    chartDescription() {
      if (!this.hasHistory) return 'Not enough price history';
      if (this.summary.state === constants.MARKET_OPEN)
        return `Last 30 days: ${this.tile.left.name} ${this.tile.left.display}; ${this.tile.right.name} ${this.tile.right.display}`;
      return `Price history ${this.axisDates.start} to ${this.axisDates.end}: ${this.summary.series.map((series) => `${series.name} ${series.display}`).join('; ')}`;
    },
    raceLinkStyle() {
      return this.summary.winner
        ? this.contractStyle(this.summary.winner)
        : {
            '--contract-color-light': 'var(--h2h-neutral)',
            '--contract-color-dark': 'var(--h2h-neutral)',
          };
    },
    markers() {
      const time =
        this.summary.start + (this.summary.now - this.summary.start) * this.animationProgress;
      return this.summary.series.map((series) => {
        if (series.current === null) return null;
        if (this.summary.state !== constants.MARKET_OPEN) {
          if (this.animationProgress === 1 || time >= series.endpointTime)
            return { x: this.x(series.endpointTime), price: series.current };
        }
        if (!series.hasHistory || this.animationProgress === 1)
          return { x: CHART_WIDTH, price: series.current };
        const before = series.points.filter((point) => point.time <= time).at(-1);
        const after = series.points.find((point) => point.time > time);
        if (!before || before.price === null) return null;
        if (!after) return { x: CHART_WIDTH * this.animationProgress, price: before.price };
        if (after.price === null) return null;
        const fraction = (time - before.time) / (after.time - before.time);
        return {
          x: CHART_WIDTH * this.animationProgress,
          price: before.price + (after.price - before.price) * fraction,
        };
      });
    },
    endLabelY() {
      const values = this.markers.map((marker) => (marker ? this.y(marker.price) : null));
      if (
        values.every((value) => value !== null) &&
        Math.abs(values[0] - values[1]) < END_LABEL_MIN_GAP
      ) {
        const center = Math.max(
          END_LABEL_CENTER_BOUNDS.min,
          Math.min(END_LABEL_CENTER_BOUNDS.max, (values[0] + values[1]) / 2),
        );
        const offset = END_LABEL_MIN_GAP / 2;
        return values[0] <= values[1]
          ? [center - offset, center + offset]
          : [center + offset, center - offset];
      }
      return values;
    },
  },
  watch: {
    chartRecords() {
      this.refreshChart();
    },
    'tile.left.price'() {
      this.clock = Date.now();
    },
    'tile.right.price'() {
      this.clock = Date.now();
    },
  },
  methods: {
    identityLabel(series) {
      const name = `${series.name}${series.partyLabel ? ` (${series.partyLabel})` : ''}`;
      if (this.summary.state === constants.MARKET_SETTLED) {
        if (series.isWinner) return `${name}, Winner`;
        if (series.isDefeated) return `${name}, Defeated`;
      }
      return name;
    },
    isSummaryTradeDisabled(side) {
      return this.summary.state !== constants.MARKET_OPEN || this.isTradeDisabled(side);
    },
    tradeSummary(side, event) {
      if (this.isSummaryTradeDisabled(side)) return;
      this.trade(side, event);
    },
    endpointLabelX(marker) {
      return this.summary.state === constants.MARKET_OPEN
        ? CHART_WIDTH + CHART_LABEL_GAP
        : marker.x + CHART_LABEL_GAP;
    },
    x(time) {
      if (this.summary.now === this.summary.start) return CHART_WIDTH;
      return (CHART_WIDTH * (time - this.summary.start)) / (this.summary.now - this.summary.start);
    },
    async refreshChart() {
      this.stopAnimation();
      const generation = this.animationGeneration;
      this.chartReady = true;
      await this.$nextTick();
      if (generation === this.animationGeneration && this.hasHistory) this.prepareAnimation();
    },
    cancelHoverAnimation() {
      if (this.hoverTimer !== null) clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    },
    queueHoverAnimation(event) {
      this.cancelHoverAnimation();
      if (
        event.pointerType !== 'mouse' ||
        !window.matchMedia('(hover: hover)').matches ||
        !this.canReplayAnimation()
      )
        return;
      this.hoverTimer = setTimeout(() => {
        this.hoverTimer = null;
        if (!this.canReplayAnimation()) return;
        this.animationProgress = 0;
        this.playAnimation();
      }, HOVER_DELAY_MS);
    },
    canReplayAnimation() {
      return (
        this.hasHistory &&
        !this.motionPreference?.matches &&
        this.animationProgress === 1 &&
        (this.animationFinishedAt === null ||
          performance.now() - this.animationFinishedAt >= REPLAY_COOLDOWN_MS)
      );
    },
    stopAnimation() {
      this.cancelHoverAnimation();
      this.animationGeneration += 1;
      cancelAnimationFrame(this.animationFrame);
      this.chartObserver?.disconnect();
      this.animationProgress = 1;
    },
    prepareAnimation() {
      if (this.motionPreference?.matches) return;
      this.animationProgress = 0;
      if (typeof IntersectionObserver === 'undefined') return this.playAnimation();
      const generation = this.animationGeneration;
      const observer = new IntersectionObserver(
        (entries) => {
          if (generation !== this.animationGeneration) return;
          if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            this.playAnimation();
          }
        },
        { threshold: ANIMATION_VISIBILITY_THRESHOLD },
      );
      this.chartObserver = observer;
      observer.observe(this.$refs.chart);
    },
    playAnimation() {
      if (this.motionPreference?.matches) return this.stopAnimation();
      const generation = this.animationGeneration;
      const start = performance.now();
      const frame = (now) => {
        if (generation !== this.animationGeneration) return;
        const fraction = Math.min(1, (now - start) / ANIMATION_DURATION_MS);
        this.animationProgress = 1 - (1 - fraction) ** ANIMATION_EASING_EXPONENT;
        if (fraction < 1) this.animationFrame = requestAnimationFrame(frame);
        else {
          this.animationFrame = null;
          this.animationFinishedAt = now;
        }
      };
      this.animationFrame = requestAnimationFrame(frame);
    },
    onMotionPreferenceChange() {
      if (this.motionPreference.matches) this.stopAnimation();
    },
    contractStyle(side) {
      if (side.isDefeated)
        return {
          '--contract-color-light': 'var(--h2h-defeated)',
          '--contract-color-dark': 'var(--h2h-defeated)',
        };
      // Compute both palettes; only body[data-theme] in CSS selects the active one.
      return {
        '--contract-color-light': side.color,
        '--contract-color-dark': readableContractColor(side.color, true),
      };
    },
    y(price) {
      return CHART_TOP + (PRICE_MAX - price) * (CHART_HEIGHT / PRICE_MAX);
    },
    chartPath(series) {
      let drawing = false;
      return series.points
        .map((point) => {
          if (point.price === null) {
            drawing = false;
            return '';
          }
          const x = this.x(point.time).toFixed(SVG_COORDINATE_PRECISION);
          const y = this.y(point.price).toFixed(SVG_COORDINATE_PRECISION);
          const command = drawing ? 'L' : 'M';
          drawing = true;
          return `${command}${x},${y}`;
        })
        .join(' ');
    },
  },
  mounted() {
    this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.motionPreference.addEventListener('change', this.onMotionPreferenceChange);
    this.refreshChart();
    this.clockTimer = setInterval(() => {
      this.clock = Date.now();
    }, CLOCK_INTERVAL_MS);
  },
  beforeUnmount() {
    this.stopAnimation();
    this.motionPreference?.removeEventListener('change', this.onMotionPreferenceChange);
    clearInterval(this.clockTimer);
  },
};
